import {
  DocumentNode,
  MutationHookOptions,
  QueryHookOptions,
  TypedDocumentNode,
  useMutation,
  useQuery,
} from '@apollo/client'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  BulkUpdateOrderStatusInput,
  BulkUpdateOrderStatusMutationDocument,
  BulkUpdateOrderStatusMutationMutation,
  BulkUpdateOrderStatusMutationMutationVariables,
  OrderFilterInput,
  OrderReviewGigaQueryDocument,
  OrderStatus,
  PaymentOrderInfoDocument,
} from '__generated__/atlas-types'

import {
  OrderDeliveryInfo,
  OrderDetails,
  UpdateOrderDeliveryInfoInput,
  UpdateOrderInfoInput,
} from 'types'
import { toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { useRenderToasters } from 'hooks/useRenderToasters'
import { getAccountId } from 'store/currentUser/selectors'

export const useBrandId = (): string => {
  const accountId = useSelector(getAccountId)
  const brandId = toGlobalId('Brand', accountId) ?? ''

  return brandId
}

const STANDARD_ERROR_MSG = 'Error updating the order.'
const STANDARD_SUCCESS_MSG = 'Order updated successfully.'

type ResponseMessages = {
  success?: string
  error?: string
}

export const useOrderId = (): string => {
  const { id } = useParams<{ id: string }>()
  return toGlobalId('Order', id) ?? ''
}

export const useOrdersQuery = <
  T,
  V,
  K extends { filters: OrderFilterInput } & V,
>(
  query: TypedDocumentNode<T, K>,
  variables?: V,
  options?: QueryHookOptions<T, K>,
) => {
  const orderId = useOrderId()

  return useQuery(query, {
    client: atlasClient,
    variables: {
      filters: {
        ids: [orderId],
      },
      ...variables,
    } as K,
    skip: !orderId,
    ...options,
  })
}

export type UpdateOrdersDetailsResult = {
  orderDetails: OrderDetails
  messages: ResponseMessages
}

export type UseOrdersMutationReturnType = {
  isUpdating: boolean
  onUpdate: ({
    orderIds,
    orderDetails,
  }: OnUpdateParams) => Promise<UpdateOrdersDetailsResult>
}

export type OnUpdateParams = {
  orderIds: string[]
  orderDetails: OrderDetails
  refetchQueries?: {
    query: DocumentNode
    variables?: {}
  }[]
}

export const useOrdersMutation = (
  mutation: DocumentNode,
  options?: MutationHookOptions<UseOrdersMutationReturnType, {}>,
): UseOrdersMutationReturnType => {
  const [updateOrderInfo, { loading, error: apolloError }] = useMutation(
    mutation,
    {
      ...options,
      client: atlasClient,
    },
  )

  const messages: ResponseMessages = {
    error: apolloError && STANDARD_ERROR_MSG,
  }

  const onUpdate = async ({
    orderIds,
    orderDetails,
    refetchQueries = [],
  }: OnUpdateParams): Promise<UpdateOrdersDetailsResult> => {
    try {
      if (
        !orderIds.length ||
        !orderDetails ||
        Object.keys(orderDetails).length === 0
      ) {
        throw new Error(STANDARD_ERROR_MSG)
      }

      const input: UpdateOrderInfoInput = {
        orderIdentifiers: {
          orderIds,
        },
        orderDetails,
      }

      const { errors } = await updateOrderInfo({
        variables: {
          input,
        },
        ...(refetchQueries?.length && {
          refetchQueries: [...refetchQueries],
        }),
      })

      if (errors) {
        throw new Error(STANDARD_ERROR_MSG)
      }

      messages.success = STANDARD_SUCCESS_MSG
    } catch (error) {
      messages.error = (error as Error).message
    } finally {
      return { orderDetails, messages }
    }
  }

  return {
    isUpdating: loading,
    onUpdate,
  }
}

export type UseOrderDeliveryMutationReturnType = {
  isUpdatingDeliveryInfo: boolean
  onUpdateOrderDeliveryInfo: (
    orderDeliveryInfo: OrderDeliveryInfo,
    orderId: string | null,
  ) => Promise<OrderDeliveryInfo | undefined>
}

export const useOrderDeliveryMutation = (
  mutation: DocumentNode,
): UseOrderDeliveryMutationReturnType => {
  const [updateOrderDelivery, { loading }] = useMutation(mutation, {
    client: atlasClient,
  })

  const { renderErrorToast } = useRenderToasters()

  const onUpdateOrderDeliveryInfo = async (
    orderDeliveryInfo: OrderDeliveryInfo,
    orderId: string | null,
  ): Promise<OrderDeliveryInfo | undefined> => {
    try {
      if (
        !orderId ||
        !orderDeliveryInfo ||
        Object.keys(orderDeliveryInfo).length === 0
      ) {
        throw new Error(STANDARD_ERROR_MSG)
      }

      const input: UpdateOrderDeliveryInfoInput = {
        orderId,
        orderDeliveryInfo,
      }

      const { errors } = await updateOrderDelivery({
        variables: {
          input,
        },
        update: (cache) => {
          if (!orderDeliveryInfo.purchaseOrderCode) {
            return
          }
          cache.modify({
            id: cache.identify({
              __typename: 'Order',
              id: orderId,
            }),
            fields: {
              referenceNumber: () => orderDeliveryInfo.purchaseOrderCode!,
            },
          })
        },
      })

      if (errors) {
        throw new Error(STANDARD_ERROR_MSG)
      }

      return orderDeliveryInfo
    } catch (error) {
      renderErrorToast({
        title: 'Error',
        description: (error as Error).message,
      })
    }
  }

  return {
    isUpdatingDeliveryInfo: loading,
    onUpdateOrderDeliveryInfo,
  }
}

export const useBulkUpdateOrderStatus = (
  options?: MutationHookOptions<
    BulkUpdateOrderStatusMutationMutation,
    BulkUpdateOrderStatusMutationMutationVariables
  >,
) => {
  const [updateOrdersStatus, { loading }] = useMutation(
    BulkUpdateOrderStatusMutationDocument,
    {
      client: atlasClient,
      refetchQueries: [PaymentOrderInfoDocument, OrderReviewGigaQueryDocument],
      ...options,
    } as any,
  )

  const bulkUpdateOrderStatus = ({
    orderIds,
    status,
    sendToBrand,
    sendToRetailer,
  }: {
    orderIds: string[]
    status: OrderStatus
    sendToBrand?: boolean
    sendToRetailer?: boolean
  }) => {
    const input: BulkUpdateOrderStatusInput = {
      orderIds,
      status,
      sendToBrand,
      sendToRetailer,
    }

    return updateOrdersStatus({
      variables: {
        input,
      },
    })
  }

  return {
    isUpdating: loading,
    bulkUpdateOrderStatus,
  }
}
